// src/ecrans/nest.jsx — LES PIÈCES COMMUNES DES ÉCRANS NEST (maquette v677).
//
// Couleurs, en-tête blanc à tuiles, rubriques repliables, barre de
// sous-onglets, états de lecture. Mesurées sur la maquette
// (docs/reference/maquette-nest-v677.html) à 1280 et 390 px, jamais reprises
// de l'appli. Une couleur = un sens (maquette-v677-decisions.md) :
//   vert = fait, bon, à lire · aubergine = une action attend · gris = neutre.
// Les plateformes gardent leurs couleurs (Airbnb, Booking, Direct).

import { useState } from "react";

export const C = Object.freeze({
  encre: "#17211E",
  gris: "#5E6A66",
  grisD: "#6B7672",
  bord: "#E6EAE8",
  fond: "#EEF2EF",
  fondCarte: "#F8F9F8",
  vert: "#087F6D",
  vertF: "#0B5A49",
  vertTx: "#2F6B5C",
  vertP: "#E8F3EE",
  vertTrait: "#94C77F",
  aub: "#7A2E6E",
  aubTx: "#5E2255",
  aubP: "#F4E7F1",
  aubBord: "#DDC2D7",
  segFond: "#F2F4F3",
  // Les rubriques calmes de l'Accueil : « en vert nuit, pas en gris terne »
  // (feuille de style de la maquette, après le gris du journal).
  nuit: "#143D34",
});

/**
 * Le mot au singulier ou au pluriel : 0 et 1 au singulier (maquette) ; un
 * nombre inconnu (« — », « … », null) au pluriel : « — nuits libres ».
 */
export function mot(n, s, p) {
  return typeof n === "number" && n <= 1 ? s : p || `${s}s`;
}

/** « 1 nuit », « 2 nuits » : LA fonction `pl` de la maquette. */
export function pl(n, s, p) {
  return `${n} ${mot(n, s, p)}`;
}

/** Couleurs des plateformes (exception voulue de la maquette, 14/10). */
export const PLATEFORMES = Object.freeze({
  airbnb: { nom: "Airbnb", fond: "#FAE5E5", bord: "#F1A0A1", tx: "#C94A4A", point: "#EC6564" },
  booking: { nom: "Booking", fond: "#D7DFFB", bord: "#7D8DCD", tx: "#2B3A78", point: "#2B3A78" },
  direct: { nom: "Direct", fond: "#E8F3EE", bord: "#94C77F", tx: "#0B5A49", point: "#0F7A64" },
  ferme: { nom: "Bloqué", fond: "#F4F6F5", bord: "#CDD4D1", tx: "#5E6A66", point: "#98A2AD" },
});

/** La plateforme d'un séjour, d'après sa source ; « autre » si inconnue. */
export function plateformeDe(source, type) {
  if (type === "ferme" || type === "fermeture" || type === "blocage") return "ferme";
  const s = String(source || "").toLowerCase();
  if (type === "direct" || s === "direct" || s.includes("site")) return "direct";
  if (s.includes("airbnb")) return "airbnb";
  if (s.includes("booking")) return "booking";
  return "autre";
}

const CSS = `
.nest{font-family:Manrope,-apple-system,BlinkMacSystemFont,sans-serif;color:${C.encre};background:${C.fond};min-height:100%}
.nest *{box-sizing:border-box}
.nest button{font-family:inherit}
.nest-tete{background:#fff;padding:18px 24px;border-bottom:1px solid ${C.bord}}
.nest-sous{font-size:11.5px;color:${C.grisD};margin:0}
.nest-titre{font-size:19px;font-weight:600;margin:2px 0 0;color:${C.encre}}
.nest-tuiles{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap}
.nest-tuile{all:unset;box-sizing:border-box;cursor:pointer;display:block;text-align:left;background:${C.vertP};border-radius:12px;padding:10px 14px;min-width:110px}
.nest-tuile b{display:block;font-size:22px;font-weight:600;line-height:1.1;color:${C.vertF}}
.nest-tuile b small{font-size:12px;font-weight:500;color:${C.vertTx}}
.nest-tuile s{display:block;font-size:11px;color:${C.vertTx};text-decoration:none;margin-top:3px}
.nest-tuile i{display:block;font-style:normal;font-size:11px;color:${C.encre};margin-top:4px}
.nest-tuile.al{background:${C.aubP}}
.nest-tuile.al b{color:${C.aub}}
.nest-tuile.al s{color:${C.aub}}
.nest-tuile.on{box-shadow:inset 0 0 0 1.5px ${C.vertTrait}}
.nest-tuile:focus-visible{outline:2px solid ${C.vert};outline-offset:2px}
.nest-page{padding:14px 15px 24px}
.nest-carte{background:#fff;border:1px solid ${C.bord};border-radius:14px;padding:11px 12px}
.nest-rub{background:#fff;border:1px solid #E3E8E5;border-radius:16px;margin-bottom:8px;box-shadow:0 1px 2px rgba(23,33,30,.04)}
.nest-rubH{all:unset;box-sizing:border-box;cursor:pointer;display:flex;align-items:center;gap:8px;width:100%;padding:12px 14px;min-height:46px}
.nest-rubI{width:28px;height:28px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#fff}
.nest-rubT{flex:1;font-size:13.5px;font-weight:600;margin:0}
.nest-pas{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 8px;border-radius:20px;font-size:11.5px;font-weight:700;color:#fff}
.nest-rubC{padding:0 14px 12px}
.nest-ligne{all:unset;box-sizing:border-box;cursor:pointer;display:flex;align-items:center;gap:10px;width:100%;background:#fff;border:1px solid ${C.bord};border-radius:12px;padding:9px 11px;margin-bottom:6px;min-height:44px}
.nest-ligne .t{display:block;font-size:12.5px;font-weight:600}
.nest-ligne .s{display:block;font-size:11px;color:${C.gris};margin-top:2px}
.nest-ligne .co{flex:1;min-width:0}
.nest-ligne .ch{color:${C.grisD}}
.nest-bascule{display:flex;background:#fff;border-bottom:1px solid ${C.bord};overflow-x:auto}
.nest-bascule button{all:unset;cursor:pointer;flex:1;display:flex;align-items:center;justify-content:center;gap:7px;padding:0 15px;min-height:46px;font-size:12.5px;color:${C.gris};white-space:nowrap;border-bottom:2px solid transparent}
.nest-bascule button.on{color:${C.encre};font-weight:600;border-bottom-color:${C.vert}}
.nest-nb{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;border-radius:9px;font-size:10.5px;font-weight:700;background:${C.aub};color:#fff}
.nest-seg{display:inline-flex;gap:3px;background:${C.segFond};border-radius:10px;padding:3px;flex-wrap:wrap}
.nest-seg button{all:unset;cursor:pointer;display:flex;align-items:center;gap:6px;padding:0 13px;min-height:38px;border-radius:8px;font-size:12px;color:${C.gris}}
.nest-seg button.on{background:#fff;color:${C.encre};font-weight:600;box-shadow:0 1px 3px rgba(23,33,30,.12)}
.nest-bt{all:unset;box-sizing:border-box;cursor:pointer;display:inline-flex;align-items:center;gap:6px;padding:0 13px;min-height:44px;border-radius:10px;font-size:12px;font-weight:600;background:#fff;border:1px solid #D6DDDA;color:#1C3A33}
.nest-bt.p{background:${C.vert};color:#fff;border-color:transparent}
.nest-bt[disabled]{opacity:.55;cursor:not-allowed}
.nest-sel{min-height:38px;border:1px solid #DCE3DF;border-radius:9px;padding:0 8px;font-size:12px;background:#fff;color:${C.encre};font-family:inherit}
.nest-champ{min-height:38px;border:1px solid #DCE3DF;border-radius:9px;padding:0 10px;font-size:12px;background:#fff;color:${C.encre};font-family:inherit;flex:1;min-width:160px}
.nest-etat{font-size:12px;color:${C.gris};padding:10px 2px}
.nest-erreur{display:flex;gap:8px;align-items:flex-start;font-size:12px;color:${C.aubTx};background:${C.aubP};border:1px solid ${C.aubBord};border-radius:10px;padding:9px 11px;margin:6px 0}
.nest-erreur button{all:unset;cursor:pointer;text-decoration:underline;font-weight:600;margin-left:auto;white-space:nowrap}
.nest-manque{font-size:11px;color:${C.grisD};font-style:italic}
.nest-inter{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${C.grisD};margin:14px 0 7px}
@media (max-width:640px){
 .nest-tete{padding:16px 15px}
 .nest-tuiles{display:grid;grid-template-columns:1fr 1fr}
 .nest-tuile{min-width:0}
 .nest-bascule button{flex:0 0 auto}
}
`;

/** La feuille de style des écrans NEST, posée une fois par écran. */
export function StyleNest() {
  return <style>{CSS}</style>;
}

/** Une icône dessinée (jamais un emoji), avec son nom pour les lecteurs. */
export function Ic({ d, t = 15, titre }) {
  return (
    <svg
      width={t}
      height={t}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={titre ? undefined : "true"}
      role={titre ? "img" : undefined}
    >
      {titre ? <title>{titre}</title> : null}
      {d}
    </svg>
  );
}

export const ICO = {
  attend: (
    <>
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </>
  ),
  msg: <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2Z" />,
  tache: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  oeil: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  loupe: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  gauche: <path d="m15 18-6-6 6-6" />,
  droite: <path d="m9 18 6-6-6-6" />,
  horloge: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  controle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>
  ),
  echange: (
    <>
      <path d="M7 4 3 8l4 4" />
      <path d="M3 8h14" />
      <path d="m17 20 4-4-4-4" />
      <path d="M21 16H7" />
    </>
  ),
  boite: (
    <>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </>
  ),
  liste: (
    <>
      <path d="M9 6h12" />
      <path d="M9 12h12" />
      <path d="M9 18h12" />
      <path d="M4 6h.01" />
      <path d="M4 12h.01" />
      <path d="M4 18h.01" />
    </>
  ),
  regles: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h10" />
      <path d="M4 17h13" />
    </>
  ),
  etoile: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9L12 3Z" />,
  cadenas: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
};

/** L'en-tête blanc d'une zone : sous-titre, titre, tuiles. */
export function EnTete({ sous, titre, children }) {
  return (
    <div className="nest-tete">
      {sous ? <p className="nest-sous">{sous}</p> : null}
      <h2 className="nest-titre">{titre}</h2>
      {children ? <div className="nest-tuiles">{children}</div> : null}
    </div>
  );
}

/**
 * Une tuile chiffrée. `n` vaut « … » en lecture et « — » quand la lecture a
 * échoué ou n'existe pas encore : jamais 0 à la place d'une inconnue.
 * `alerte` = une action attend (aubergine).
 */
export function Tuile({ n, sur, libelle, detail, alerte, ouverte, onClick, testId, titre }) {
  const cls = ["nest-tuile", alerte ? "al" : "", ouverte ? "on" : ""].join(" ").trim();
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      data-testid={testId}
      title={titre}
      aria-expanded={ouverte === undefined ? undefined : Boolean(ouverte)}
    >
      <b>
        {n}
        {sur !== undefined ? <small> / {sur}</small> : null}
      </b>
      <s>{libelle}</s>
      {detail ? <i>{detail}</i> : null}
    </button>
  );
}

/** Une rubrique repliable de l'Accueil (en-tête blanc, icône et compteur colorés). */
export function Rubrique({ titre, n, ton = "gris", ic, ouverteDepart = false, children, testId }) {
  const [ouverte, setOuverte] = useState(ouverteDepart);
  const fond = ton === "aub" ? C.aub : ton === "vert" ? C.vert : C.nuit;
  return (
    <section className="nest-rub" data-testid={testId}>
      <button
        type="button"
        className="nest-rubH"
        aria-expanded={ouverte}
        onClick={() => setOuverte((v) => !v)}
      >
        <span className="nest-rubI" style={{ background: fond }}>
          <Ic d={ic} />
        </span>
        <h3 className="nest-rubT">{titre}</h3>
        <span className="nest-pas" style={{ background: fond }}>
          {n}
        </span>
        <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1, color: C.encre, width: 12, textAlign: "center" }}>
          {ouverte ? "▾" : "▸"}
        </span>
      </button>
      {ouverte ? <div className="nest-rubC">{children}</div> : null}
    </section>
  );
}

/** La barre des sous-onglets d'une zone (Opérations, Voyageurs). */
export function Bascule({ entrees, actif, onChoisir, testId }) {
  return (
    <div className="nest-bascule" role="tablist" data-testid={testId}>
      {entrees.map((e) => (
        <button
          key={e.id}
          type="button"
          role="tab"
          aria-selected={actif === e.id}
          className={actif === e.id ? "on" : ""}
          onClick={() => onChoisir(e.id)}
        >
          {e.ic ? <Ic d={e.ic} t={15} /> : null}
          {e.libelle}
          {e.nb ? <span className="nest-nb">{e.nb}</span> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * L'état d'une lecture qui n'est pas « ok » : « On lit… » ou l'erreur, avec
 * « Réessayer ». Rend null quand la lecture a réussi.
 */
export function EtatLecture({ lecture, quoi, onRelire }) {
  if (!lecture || lecture.etat === "ok") return null;
  if (lecture.etat === "lecture")
    return (
      <p className="nest-etat" role="status">
        On lit…
      </p>
    );
  return (
    <div className="nest-erreur" role="alert">
      <span>
        {quoi ? <b>{quoi} : </b> : null}
        {lecture.motif || "lecture impossible."}
      </span>
      {onRelire ? (
        <button type="button" onClick={onRelire}>
          Réessayer
        </button>
      ) : null}
    </div>
  );
}

/**
 * Le lien vers l'ANCIEN écran, tant que les portes d'écriture n'existent pas :
 * aucun geste n'est retiré à l'admin (bloquer, confier, créer, cocher…).
 */
export function LienAncien({ pour, onClick, testId }) {
  if (!onClick) return null;
  return (
    <button type="button" className="nest-bt" onClick={onClick} data-testid={testId} style={{ fontWeight: 500 }}>
      Ancien écran (pour {pour}) ›
    </button>
  );
}

/** Une place que la maquette prévoit mais dont la porte n'existe pas encore. */
export function PorteManquante({ texte }) {
  return <p className="nest-manque">— {texte}</p>;
}

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
export { MOIS };

/** « 2026-08-23 » → Date à midi UTC (jamais décalée par le fuseau). */
export function dateIso(iso) {
  const [a, m, j] = String(iso).split("-").map(Number);
  return new Date(Date.UTC(a, m - 1, j, 12));
}

/** « Dimanche 23 août » */
export function jourEnLettres(iso) {
  const d = dateIso(iso);
  const s = `${JOURS[d.getUTCDay()]} ${d.getUTCDate()} ${MOIS[d.getUTCMonth()]}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** « 23 août » */
export function jourCourt(iso) {
  const d = dateIso(iso);
  return `${d.getUTCDate()} ${MOIS[d.getUTCMonth()]}`;
}

/** Aujourd'hui à Paris, « AAAA-MM-JJ ». */
export function aujourdhuiParis(maintenant = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(maintenant);
}

/** Le jour décalé de n jours. */
export function decalerIso(iso, n) {
  const d = dateIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
